import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/Base/Form";
import { Link } from "react-router-dom";
import { Button } from "@/components/Base/Button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/store/useAuth";
import { FieldType, InputFields } from "@/components/Common/form/InputFields";
import { AuthFormHeader } from "@/components/Auth/AuthFormHeader";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { Spinner } from "@/components/Common/Spinner";

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email" }),
});

const fields: FieldType<z.infer<typeof formSchema>>[] = [
  {
    name: "email",
    label: "Email",
    type: "email",
    placeholder: "Email",
    required: true,
    autoComplete: "email",
    autoFocus: true,
  },
];

const initialValues = {
  email: "",
};

export const RequestPasswordResetForm = () => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const { authError, requestPasswordReset } = useAuth();

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    return await requestPasswordReset(values.email);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex min-h-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-8 rounded-xl border-2 border-white bg-secondary/10 p-8">
            <AuthFormHeader
              title="Request Password Reset"
              instructions={
                "Enter your email below. If it matches out records, we will send you a link to reset your password."
              }
            />
            <div className="mt-6 flex flex-col justify-center gap-4"></div>

            <InputFields fields={fields} form={form} />

            <div
              className={cn(
                "flex w-96 flex-row items-center gap-4 rounded-md bg-destructive/20 p-4 text-destructive",
                !authError && "invisible"
              )}
            >
              <Icon name={IconNames.warning} size={28} />
              <div className="w-128 text-sm">{authError}</div>
            </div>
            <div className="flex h-24 flex-row justify-center">
              {form.formState.isSubmitting && <Spinner />}
              {form.formState.isSubmitted &&
                (!authError || authError === "") && (
                  <div className="text-md rounded-md border border-success bg-success/20 p-4 text-center text-white">
                    If the email you provided matches our records, you will
                    receive a password reset link shortly.
                  </div>
                )}
            </div>

            <div className={"flex flex-row justify-between"}>
              <Link to="/login">
                <Button type={"button"}>Login</Button>
              </Link>
              <Button
                variant={"accent"}
                type={"submit"}
                disabled={
                  form.formState.isSubmitting ||
                  (form.formState.isSubmitted &&
                    (authError === "" || !authError))
                }
              >
                Request Reset
              </Button>
            </div>
            <div className={"flex flex-row justify-center pt-4"}>
              <p>
                Don&#39;t have an account?{" "}
                <Link to="/register" className={"underline"}>
                  Register
                </Link>{" "}
                to get started.
              </p>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
